// SPDX-License-Identifier: AGPL-3.0-or-later
import { resolveActiveItems } from './conditional.js';
import { runFieldPass } from './field-validators.js';
import { runCrossFieldPass } from './cross-field.js';
import { runCompletenessPass } from './completeness.js';
import { runFreshnessPass } from './freshness.js';
/**
 * Pure synchronous engine. Runs the six passes from PLAN.md §5 in order
 * and returns a {@link ValidationResult}. Async results, if provided by
 * the orchestrator, are merged in as the final pass.
 *
 *   1. Conditional resolution (enableWhen / enableBehavior)
 *   2. Field-level (type / required / regex / min / max)
 *   3. Cross-field (whitelisted expression contradiction rules)
 *   4. Completeness (form-level rollup of unanswered required items)
 *   5. Freshness (answered-at vs. freshness-days threshold)
 *   6. Async (merge pre-resolved validator results from context)
 *
 * The engine itself never calls into the network; the async pass is purely
 * a merge step. The orchestrator is responsible for invoking registered
 * async validators (see `async-registry.ts`) and supplying the results.
 */
export function validate(context) {
    const { questionnaire, response, now, defaultFreshnessDays, defaultPhoneCountry, asyncResults } = context;
    // 1. Conditional resolution.
    const activeLinkIds = resolveActiveItems(questionnaire, response);
    // 2. Field-level.
    const fieldFindings = runFieldPass(questionnaire, response, activeLinkIds, {
        now,
        defaultPhoneCountry,
    });
    // 3. Cross-field.
    const crossFieldFindings = runCrossFieldPass(questionnaire, response);
    // 4. Completeness (form-level rollup).
    const completenessFindings = runCompletenessPass(questionnaire, response, activeLinkIds);
    // 5. Freshness.
    const freshnessFindings = runFreshnessPass(questionnaire, response, activeLinkIds, now, defaultFreshnessDays);
    // 6. Async — merge pre-resolved results.
    const asyncFindings = [];
    if (asyncResults !== undefined) {
        for (const [, result] of asyncResults) {
            asyncFindings.push(...result.findings);
        }
    }
    const findings = [
        ...fieldFindings,
        ...crossFieldFindings,
        ...completenessFindings,
        ...freshnessFindings,
        ...asyncFindings,
    ];
    const ok = !findings.some((f) => f.severity === 'error');
    return { ok, findings };
}
//# sourceMappingURL=engine.js.map